import { Alert, alpha, Box, Chip, Stack } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@bw/shared/hooks";
import type { ReactNode } from "react";
import { gamificationApi } from "../api";

export function PublicGamificationFrame({
  userId,
  children,
}: {
  userId: string;
  children: ReactNode;
}) {
  const { user } = useAuth();
  const publicStyle = useQuery({
    queryKey: ["gamification", "public", user?.uid ?? null, userId],
    queryFn: () => gamificationApi.public(userId),
    enabled: Boolean(user),
    retry: false,
  });

  // One container for every state, so a late style failure never remounts the
  // profile (and never closes a dialog or menu the player has open).
  const failed = publicStyle.isError;
  const style = failed ? undefined : publicStyle.data;
  const accent = style?.profile_accent === "steady_green";
  const ringColor =
    style?.avatar_ring === "competitor"
      ? "warning.main"
      : style?.avatar_ring === "verified_play"
        ? "primary.main"
        : "transparent";

  return (
    <Box
      component="section"
      aria-label="Player profile and verified highlights"
      sx={{
        p: { xs: 1.5, sm: 2 },
        border: "1px solid",
        borderColor: accent ? "primary.dark" : "divider",
        borderRadius: 3,
        background: (theme) =>
          accent
            ? `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.12)}, ${alpha(theme.palette.primary.main, 0.015)} 48%)`
            : "transparent",
        "& .MuiAvatar-root": {
          boxShadow:
            ringColor === "transparent"
              ? "none"
              : (theme) =>
                  `0 0 0 3px ${theme.palette.background.paper}, 0 0 0 6px ${
                    ringColor === "warning.main"
                      ? theme.palette.warning.main
                      : theme.palette.primary.main
                  }`,
        },
        "@media (prefers-reduced-motion: reduce)": {
          "&, & *": { transition: "none !important" },
        },
      }}
    >
      {failed ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Profile style is temporarily unavailable. Player details are
          unchanged.
        </Alert>
      ) : null}
      {style?.featured_achievement || style?.streak_weeks != null ? (
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          sx={{ mb: 2, flexWrap: "wrap" }}
        >
          {style.featured_achievement ? (
            <Chip
              color="primary"
              variant="outlined"
              label={style.featured_achievement.title}
            />
          ) : null}
          {style.streak_weeks != null ? (
            <Chip
              variant="outlined"
              label={`${style.streak_weeks} week verified-play streak`}
            />
          ) : null}
        </Stack>
      ) : null}
      {children}
    </Box>
  );
}
